import { useQuery } from "@tanstack/react-query";
import { fetchDailyRival, type DailyRivalStatusResponse } from "../api/campaignApi";

/** How often the Campaign nav dot re-checks the daily rival (ms). */
const DAILY_RIVAL_POLL_MS = 60_000;
const DAILY_RIVAL_STALE_MS = 30_000;

/**
 * React Query key prefix for the daily-rival status. Exported so battle completion can
 * invalidate it immediately — otherwise the nav notification dot (and the in-campaign
 * DailyRivalCard, which shares this same query) would linger until the next poll (up to
 * a minute) after the player has already played today's rival.
 */
export const DAILY_RIVAL_QUERY_KEY = "dailyRival";

/**
 * Single source of truth for the "Rival of the Day" status — shared by the Campaign nav
 * notification dot AND the in-campaign DailyRivalCard so the two surfaces never diverge
 * (both react to the same cached query, both refresh on the same invalidation).
 */
export function useDailyRivalStatus(actingAddress: string | null) {
  return useQuery({
    queryKey: [DAILY_RIVAL_QUERY_KEY, actingAddress],
    queryFn: () => fetchDailyRival(actingAddress as string),
    enabled: !!actingAddress,
    staleTime: DAILY_RIVAL_STALE_MS,
    refetchInterval: DAILY_RIVAL_POLL_MS,
  });
}

/**
 * Whether the player has an unplayed "Rival of the Day" campaign battle available today —
 * drives the red notification dot on the Campaign nav entry (same idea as the pet-care dot on
 * the Suikamon entry). True while today's challenge exists and hasn't been claimed; clears once
 * it's played. Keyed by the acting identity.
 */
export function useDailyRivalAvailable(actingAddress: string | null): boolean {
  const { data } = useDailyRivalStatus(actingAddress);
  return !!(data?.success && data.nodeId && !data.claimed);
}

export type { DailyRivalStatusResponse };
